import dayjs, { Dayjs, extend } from 'dayjs'
import utc from 'dayjs/plugin/utc'
import React, { createContext, useCallback, useContext, useState, useEffect, useMemo } from 'react'

import { ReportType } from 'api/types/report/reportApiTypes'
import BookType from 'constants/BookType'
import { REPORT_DATE_FORMAT } from 'constants/Dates'
import { useOrg } from 'contexts/OrgContext'
import { FetchReportDataFilters, PaginatedReportParams } from 'hooks/data/report/reportHookTypes'
import useFeatureFlags from 'hooks/feature-flags/useFeatureFlags'
import isEqual from 'utils/isEqual'
import { getWalletAddrSourceFromDropdownValue } from 'utils/wallets/walletsUtils'

extend(utc)

const today = dayjs.utc()

enum ReportFilterTypes {
  DateRangeFilter = 'dateRangeFilter',
  SingleDateFilter = 'singleDateFilter',
  SourceFilter = 'sourceFilter',
  AssetFilter = 'assetFilter',
  PageSize = 'pageSize',
  PageNumber = 'pageNumber',
  BookTypeFilter = 'bookType',
  QueryExecutionId = 'queryExecutionId',
  CurrentToken = 'currentToken',
  SourceTags = 'sourceTags',
}

export type ReportFilters = {
  [ReportFilterTypes.DateRangeFilter]?: Dayjs[]
  [ReportFilterTypes.SingleDateFilter]?: Dayjs
  [ReportFilterTypes.SourceFilter]?: string
  [ReportFilterTypes.AssetFilter]?: string[]
  [ReportFilterTypes.PageSize]: number
  [ReportFilterTypes.PageNumber]: number
  [ReportFilterTypes.BookTypeFilter]: BookType
  [ReportFilterTypes.QueryExecutionId]?: string
  [ReportFilterTypes.CurrentToken]?: string
  [ReportFilterTypes.SourceTags]?: string[]
}

export const DEFAULT_REPORT_PAGE_SIZE = 25

const defaultFilterState: ReportFilters = {
  [ReportFilterTypes.DateRangeFilter]: [],
  [ReportFilterTypes.SingleDateFilter]: today,
  [ReportFilterTypes.SourceFilter]: undefined,
  [ReportFilterTypes.AssetFilter]: [],
  [ReportFilterTypes.PageSize]: DEFAULT_REPORT_PAGE_SIZE,
  [ReportFilterTypes.PageNumber]: 0,
  [ReportFilterTypes.BookTypeFilter]: BookType.Accounting,
  [ReportFilterTypes.QueryExecutionId]: '',
  [ReportFilterTypes.CurrentToken]: '',
  [ReportFilterTypes.SourceTags]: [],
}

export type ReportContext = {
  filterState: ReportFilters
  setFilters: (state: ReportFilters) => void
  getApiReportFilters: (
    reportType: ReportType,
    filterParams?: Pick<FetchReportDataFilters, 'includeAddressSource'>
  ) => FetchReportDataFilters
}

export const ReportFilterContext = createContext({} as ReportContext)

export function useReportFilter() {
  return useContext(ReportFilterContext)
}

const ReportFilterProvider = ({ children }: { children: React.ReactElement }) => {
  const [filterState, setFilterState] = useState(defaultFilterState)
  const { org } = useOrg()
  const { shouldShowReportingFormat } = useFeatureFlags()

  const {
    sourceFilter: walletFilter,
    assetFilter,
    singleDateFilter,
    dateRangeFilter,
    pageSize,
    pageNumber,
    bookType,
    sourceTags = [],
  } = filterState
  const [startDateFilter, endDateFilter] = dateRangeFilter || [undefined, undefined]

  const isSourceFiltered = walletFilter?.length
  const selectedWalletAddrsSource = isSourceFiltered
    ? getWalletAddrSourceFromDropdownValue(walletFilter)
    : undefined

  /**
   * The report filters prepped to be passed to either the
   * report or report export API hooks.
   */
  const getApiReportFilters = useCallback(
    (
      reportType: ReportType,
      filterParams?: Pick<FetchReportDataFilters, 'includeAddressSource'>
    ) => {
      const startDate = [
        ReportType.Nft,
        ReportType.SignificantHoldings,
        ReportType.QaSignificantHoldings,
        ReportType.SignificantHoldingsDetailed,
        ReportType.QaGlobalEntityHoldings,
        ReportType.QaGlobalAssetHoldings,
      ].includes(reportType)
        ? singleDateFilter
        : startDateFilter
      const endDate = [
        ReportType.Nft,
        ReportType.SignificantHoldings,
        ReportType.QaSignificantHoldings,
        ReportType.SignificantHoldingsDetailed,
        ReportType.QaGlobalEntityHoldings,
        ReportType.QaGlobalAssetHoldings,
      ].includes(reportType)
        ? undefined
        : endDateFilter

      const isRF = reportType === ReportType.QaRollForward
      // Entity-only rollup: API ignores per-source/asset filters; strip them from the payload.
      const isGlobalEntityHoldings = reportType === ReportType.QaGlobalEntityHoldings
      const isGlobalAssetHoldings = reportType === ReportType.QaGlobalAssetHoldings
      const isSignificantHoldingsReport =
        reportType === ReportType.SignificantHoldings ||
        reportType === ReportType.QaSignificantHoldings ||
        reportType === ReportType.SignificantHoldingsDetailed ||
        reportType === ReportType.QaGlobalEntityHoldings

      return {
        startDate: startDate ? dayjs(startDate).format(REPORT_DATE_FORMAT) : '',
        endDate: endDate ? dayjs(endDate).format(REPORT_DATE_FORMAT) : '',
        source:
          isRF || isGlobalEntityHoldings || isGlobalAssetHoldings
            ? ''
            : selectedWalletAddrsSource || '',
        assets: isRF || isGlobalEntityHoldings || isGlobalAssetHoldings ? [] : assetFilter || [],
        pageSize: isRF ? 999 : pageSize,
        pageNumber,
        bookType: shouldShowReportingFormat ? bookType : BookType.Tax,
        includeAddressSource: isSignificantHoldingsReport && !!filterParams?.includeAddressSource,
        sourceTags: isRF || isGlobalEntityHoldings || isGlobalAssetHoldings ? [] : sourceTags,
      }
    },
    [
      singleDateFilter,
      startDateFilter,
      endDateFilter,
      assetFilter,
      selectedWalletAddrsSource,
      pageSize,
      pageNumber,
      shouldShowReportingFormat,
      bookType,
      sourceTags,
    ]
  )

  const setFilters = useCallback(
    (newFilterState: ReportFilters) => {
      if (!isEqual(newFilterState, filterState)) {
        setFilterState(newFilterState)
      }
    },
    [filterState]
  )

  const addFilter = useCallback(
    (filterName: string, newValue: PaginatedReportParams) => {
      setFilters({
        ...filterState,
        [filterName]: newValue,
        pageNumber: 0,
        queryExecutionId: '',
        currentToken: '',
      })
    },
    [filterState, setFilters]
  )

  useEffect(() => {
    setFilterState(defaultFilterState)
  }, [org])

  const value = useMemo(
    () => ({
      filterState,
      setFilters,
      ReportFilterTypes,
      getApiReportFilters,
      addFilter,
    }),
    [filterState, getApiReportFilters, setFilters, addFilter]
  )

  return <ReportFilterContext.Provider value={value}>{children}</ReportFilterContext.Provider>
}

export default ReportFilterProvider
